import React, { useEffect, useState } from "react";
import "./mainLayour.css";
import SideBar from "./sidebar/sideBar";
import Header from "./header/header";
import LegalFooter from "./LegalFooter";
import useResponsiveShell from "./useResponsiveShell";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Building, Users, ClipboardList, Database, ActivitySquare, MessageSquareText, ShieldCheck } from "lucide-react";
import { clearCommercialControl, fetchCommercialUsage, selectCommercialUsageIdentityKey } from "../../Actions/commercialControl.action";
import { canManageCriteria, canManageWorkspaceUsers, canUseRepository } from "../../utils/commercialCapabilities";

export const headerItems = [
    { nameKey: "workspace",       link: "/workspace",       icon: <LayoutDashboard size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN","ADMIN_CLIENT","USER"], groupKey: "work" },
    { nameKey: "review_criteria", link: "/review-criteria", icon: <ClipboardList   size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN","ADMIN_CLIENT","USER"], groupKey: "configuration", requiresCriteriaManagement: true },
    { nameKey: "data_repository", link: "/data",            icon: <Database        size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN","ADMIN_CLIENT","USER"], groupKey: "configuration", requiresRepository: true },
    { nameKey: "archimede_control_center", link: "/archimede-control-center", icon: <ActivitySquare size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN"], groupKey: "monitoring" },
    { name: "Retours utilisateurs", link: "/product-feedback", icon: <MessageSquareText size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN"], groupKey: "monitoring" },
    { nameKey: "admin_commercial", link: "/admin/commercial", icon: <ShieldCheck size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN"], groupKey: "administration" },
    { nameKey: "clients",         link: "/clients",         icon: <Building        size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN"], groupKey: "administration" },
    { nameKey: "users_mgmt",      link: "/users",           icon: <Users           size={18} className="sidebar-icon" />, roles: ["SUPER_ADMIN","ADMIN_CLIENT"], groupKey: "administration", requiresWorkspaceUserManagement: true },
    { nameKey: "stage_review", link: "/stage-review", icon: null, roles: ["SUPER_ADMIN","ADMIN_CLIENT","USER"], hidden: true, groupKey: "work" },
];

function getCurrentItem(pathname) {
    return headerItems.find(item => pathname.startsWith(item.link)) || null;
}

export function canAccessHeaderItem(item, user = {}, commercialControl = {}) {
    const role = user?.role;
    const roleOk = Array.isArray(item?.roles) ? item.roles.includes(role) : true;
    if (!roleOk) return false;

    const capabilities = commercialControl.capabilities || commercialControl.usageSnapshot?.capabilities || {};

    if (item?.requiresRepository) {
        if (role === "SUPER_ADMIN") return true;
        return canUseRepository(capabilities);
    }

    if (item?.requiresCriteriaManagement) {
        if (role === "SUPER_ADMIN") return true;
        return canManageCriteria(capabilities);
    }

    if (item?.requiresWorkspaceUserManagement) {
        return canManageWorkspaceUsers(capabilities, user);
    }

    return true;
}

export default function MainLayout() {
    const user = useSelector(state => state.users.user);
    const commercialControl = useSelector(state => state.commercialControl || {});
    const commercialUsageIdentityKey = useSelector(selectCommercialUsageIdentityKey);
    const dispatch = useDispatch();
    const location = useLocation();
    const current = getCurrentItem(location.pathname);
    const { t } = useTranslation("navigation");
    const {
        closeMobileSidebar,
        isCollapsed,
        isMobileShell,
        isMobileSidebarOpen,
        toggleSidebar,
    } = useResponsiveShell(location.pathname);
    const shouldShowLayoutTitle =
        current?.nameKey && !["stage_review", "data_repository", "archimede_control_center"].includes(current.nameKey);

    useEffect(() => {
        if (!user?.id) return;
        if (
            commercialControl.usageIdentityKey &&
            commercialControl.usageIdentityKey !== commercialUsageIdentityKey
        ) {
            dispatch(clearCommercialControl());
            return;
        }
        if (
            commercialControl.usageSnapshot &&
            commercialControl.usageIdentityKey === commercialUsageIdentityKey
        ) {
            return;
        }
        if (
            commercialControl.isLoadingUsage &&
            commercialControl.loadingUsageIdentityKey === commercialUsageIdentityKey
        ) {
            return;
        }
        dispatch(fetchCommercialUsage()).catch(() => {});
    }, [
        commercialControl.isLoadingUsage,
        commercialControl.loadingUsageIdentityKey,
        commercialControl.usageIdentityKey,
        commercialControl.usageSnapshot,
        commercialUsageIdentityKey,
        dispatch,
        user?.id,
    ]);

    const visibleHeaderItems = headerItems.filter((item) =>
        canAccessHeaderItem(item, user, commercialControl)
    );

    // Premium fade-in from white when arriving from the auth handoff.
    // The login page sets the flag right before navigation; we consume it
    // on mount and play the dissolve from #fff back to the live UI.
    const [authFadeArmed, setAuthFadeArmed] = useState(() => {
        try { return sessionStorage.getItem('verdictia_auth_fade') === '1'; }
        catch (e) { return false; }
    });

    useEffect(() => {
        if (!authFadeArmed) return;
        try { sessionStorage.removeItem('verdictia_auth_fade'); } catch (e) { /* noop */ }
        // Two RAFs guarantee the initial opaque frame is painted before the
        // browser commits the transition to opacity:0 — otherwise it snaps.
        const handles = { r1: 0, r2: 0 };
        handles.r1 = window.requestAnimationFrame(() => {
            handles.r2 = window.requestAnimationFrame(() => setAuthFadeArmed(false));
        });
        return () => {
            window.cancelAnimationFrame(handles.r1);
            if (handles.r2) window.cancelAnimationFrame(handles.r2);
        };
    }, [authFadeArmed]);

    // Keep the veil mounted briefly after fade-out completes, then unmount.
    const [veilMounted, setVeilMounted] = useState(() => {
        try { return sessionStorage.getItem('verdictia_auth_fade') === '1'; }
        catch (e) { return false; }
    });
    useEffect(() => {
        if (veilMounted && !authFadeArmed) {
            const id = window.setTimeout(() => setVeilMounted(false), 850);
            return () => window.clearTimeout(id);
        }
    }, [veilMounted, authFadeArmed]);

    return (
        <div className="main-layout-shell">
            {veilMounted && (
                <div
                    className={`auth-fade-veil auth-fade-veil--reverse${authFadeArmed ? ' is-active' : ''}`}
                    aria-hidden="true"
                />
            )}
            <Header
                onToggleSidebar={toggleSidebar}
                isCollapsed={isCollapsed}
                isMobileShell={isMobileShell}
                isMobileSidebarOpen={isMobileSidebarOpen}
            />
            <div className="main-layout-body">
                <button
                    type="button"
                    className={`main-layout-sidebar-backdrop${isMobileSidebarOpen ? " is-active" : ""}`}
                    aria-label="Fermer le menu de navigation"
                    onClick={closeMobileSidebar}
                />
                <SideBar
                    currentRole={user?.role}
                    headerItems={visibleHeaderItems}
                    isCollapsed={isCollapsed}
                    isMobileOpen={isMobileSidebarOpen}
                    onNavigate={closeMobileSidebar}
                />
                <main className="outlet-css">
                    {shouldShowLayoutTitle && (
                        <h2 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1.5rem" }}>
                            {t(current.nameKey)}
                        </h2>
                    )}
                    <Outlet />
                    <LegalFooter />
                </main>
            </div>
        </div>
    );
}
